import { useState, useEffect, useCallback } from "react";
import api from "../../api/api";
import { formatRp } from "../../utils/format";
import { AlertCircle, CheckCircle2, DollarSign, FileText, AlertTriangle } from "lucide-react";
import { formatDateTime } from "../../utils/datetime";

export default function AccountantDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get("/accountant/dashboard");
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="p-6 text-[13px] text-zinc-500 flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-900" />
          Memuat ringkasan akuntansi...
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-[13px] text-rose-600 flex justify-center">
        Gagal memuat data. Silakan muat ulang.
      </div>
    );
  }

  const { summary, urgent_issues } = data;

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Ringkasan Akuntan</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Pantau status setoran kasir, masalah operasional, dan integritas stok secara keseluruhan.
        </p>
      </header>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Selisih Kas */}
        <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm flex flex-col justify-between group">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[13px] font-medium text-zinc-500">Total Selisih Kasir</div>
              <div className={`mt-3 text-3xl font-semibold tracking-tight tabular-nums ${summary.cash.total_discrepancy < 0 ? 'text-rose-600' : 'text-zinc-900'}`}>
                {formatRp(summary.cash.total_discrepancy)}
              </div>
            </div>
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${summary.cash.total_discrepancy < 0 ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-zinc-200 bg-zinc-50 text-zinc-400'}`}>
              <DollarSign className="h-4 w-4" strokeWidth={2} />
            </div>
          </div>
          <div className="mt-2 text-[11px] text-zinc-400">
            Dari {summary.cash.total_reconciled} sesi kasir
          </div>
        </div>

        {/* Omset Terverifikasi */}
        <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm flex flex-col justify-between group">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[13px] font-medium text-zinc-500">Total Uang Diterima</div>
              <div className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 tabular-nums">
                {formatRp(summary.verified_revenue)}
              </div>
            </div>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
            </div>
          </div>
          <div className="mt-2 text-[11px] text-zinc-400">
            Uang fisik yang sudah terverifikasi
          </div>
        </div>

        {/* Pending Notes */}
        <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm flex flex-col justify-between group">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[13px] font-medium text-zinc-500">Masalah Belum Selesai</div>
              <div className={`mt-3 text-3xl font-semibold tracking-tight tabular-nums ${summary.notes.total_pending > 0 ? 'text-amber-600' : 'text-zinc-900'}`}>
                {summary.notes.total_pending} <span className="text-lg font-medium text-zinc-500">Kasus</span>
              </div>
            </div>
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${summary.notes.total_pending > 0 ? 'border-amber-200 bg-amber-50 text-amber-600' : 'border-zinc-200 bg-zinc-50 text-zinc-400'}`}>
              <FileText className="h-4 w-4" strokeWidth={2} />
            </div>
          </div>
          <div className="mt-2 text-[11px] text-zinc-400">
            Catatan/temuan yang belum ditutup
          </div>
        </div>

        {/* Sesi dengan Selisih */}
        <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm flex flex-col justify-between group">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[13px] font-medium text-zinc-500">Sesi Bermasalah</div>
              <div className={`mt-3 text-3xl font-semibold tracking-tight tabular-nums ${summary.cash.sessions_with_discrepancy > 0 ? 'text-rose-600' : 'text-zinc-900'}`}>
                {summary.cash.sessions_with_discrepancy} <span className="text-lg font-medium text-zinc-500">Sesi</span>
              </div>
            </div>
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${summary.cash.sessions_with_discrepancy > 0 ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-zinc-200 bg-zinc-50 text-zinc-400'}`}>
              <AlertCircle className="h-4 w-4" strokeWidth={2} />
            </div>
          </div>
          <div className="mt-2 text-[11px] text-zinc-400">
            Sesi kasir dengan selisih kas &ne; 0
          </div>
        </div>
      </div>

      {/* Urgent Issues Table */}
      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm">
        <div className="border-b border-zinc-200 px-5 py-4 bg-zinc-50/50">
          <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Butuh Perhatian Segera
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-[13px]">
            <thead className="bg-white border-b border-zinc-200">
              <tr>
                <th className="px-4 py-3 font-medium text-zinc-500">Tanggal</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Kategori</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Catatan</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Prioritas</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Dibuat Oleh</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {urgent_issues.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-zinc-500 bg-white">
                    Tidak ada catatan yang butuh perhatian segera. Hebat! 🎉
                  </td>
                </tr>
              ) : (
                urgent_issues.map((issue) => (
                  <tr key={issue.id} className="hover:bg-zinc-50/50 transition-colors bg-white">
                    <td className="px-4 py-3 whitespace-nowrap text-zinc-700">{formatDateTime(issue.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-md bg-zinc-100 px-2 py-1 text-[11px] font-medium text-zinc-800 capitalize">
                        {issue.category.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-md truncate text-zinc-900 font-medium">{issue.note}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset ${
                        issue.priority === 'high' ? 'bg-rose-50 text-rose-700 ring-rose-600/20' :
                        issue.priority === 'medium' ? 'bg-amber-50 text-amber-700 ring-amber-600/20' :
                        'bg-zinc-50 text-zinc-700 ring-zinc-500/20'
                      }`}>
                        {issue.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{issue.created_by_name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
